import { createContext, useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import useAuth from "../hooks/useAuth";

export const WishlistContext = createContext();

export const WishlistProvider = ({ children }) => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [wishlist, setWishlist] = useState([]);
  const [loading, setLoading] = useState(true);

  // Helper to get user-specific key
  const getWishlistKey = () => user ? `wishlist_${user._id || user.email}` : 'wishlist_guest';

  // Load wishlist from localStorage when user changes
  useEffect(() => {
    if (user) {
      const savedWishlist = localStorage.getItem(getWishlistKey());
      if (savedWishlist) {
        setWishlist(JSON.parse(savedWishlist));
      } else {
        setWishlist([]);
      }
    } else {
      setWishlist([]);
    }
    setLoading(false);
    // eslint-disable-next-line
  }, [user]);

  // Save wishlist to localStorage whenever it changes and user is present
  useEffect(() => {
    if (!loading && user) {
      localStorage.setItem(getWishlistKey(), JSON.stringify(wishlist));
    }
    // eslint-disable-next-line
  }, [wishlist, loading, user]);

  // Add item to wishlist
  const addToWishlist = (product) => {
    if (!user) {
      navigate("/login?message=" + encodeURIComponent("Please sign in or register to add items to your wishlist."));
      return false;
    }

    setWishlist((prevWishlist) => {
      // Check if product already exists in wishlist
      const existingItemIndex = prevWishlist.findIndex(
        (item) => item._id === product._id
      );

      if (existingItemIndex !== -1) {
        // Product already in wishlist, do nothing
        return prevWishlist;
      } else {
        // Add new product to wishlist
        return [...prevWishlist, product];
      }
    });
    return true;
  };

  // Remove item from wishlist
  const removeFromWishlist = (productId) => {
    setWishlist((prevWishlist) => 
      prevWishlist.filter((item) => item._id !== productId)
    );
  };

  // Check if item is in wishlist
  const isInWishlist = (productId) => {
    return wishlist.some((item) => item._id === productId);
  };

  // Get total number of items in wishlist
  const getWishlistCount = () => {
    return wishlist.length;
  };

  // Clear wishlist
  const clearWishlist = () => {
    setWishlist([]);
  };

  return (
    <WishlistContext.Provider
      value={{
        wishlist,
        loading,
        addToWishlist,
        removeFromWishlist,
        isInWishlist,
        getWishlistCount,
        clearWishlist,
      }}
    >
      {children}
    </WishlistContext.Provider>
  );
};